/**
 * AutoMe AI — Floating Action Button (FAB).
 *
 * Mounts a single floating button at the bottom-left of the screen
 * (RTL "end" side) that opens the AutoMe AI chat. Idempotent — calling
 * `mount()` more than once is a no-op.
 *
 * Visibility rules:
 *   - Hidden until the user is authenticated (no AI for guests on the
 *     login screen — keeps the auth flow clean and avoids exposing the
 *     feature before the user has signed up).
 *   - Hidden while the AI chat modal itself is open (avoid stacking).
 *   - Hidden when `<body class="hide-ai-fab">` — escape hatch for screens
 *     that need the corner space (e.g. full-screen modals).
 *
 * The "חדש" badge (first 14 days) lives on top to advertise the feature.
 * After dismissal (long-press → context menu in a future iteration) it
 * could be hidden, but for now it's permanent.
 */

import { createLogger } from '../../core/logger.js';
import * as view from './aiChat.view.js';
import { iconHtml } from './icon.js';
import * as bus from '../../core/eventBus.js';
import { auth } from '../../core/firebase.js';

const log = createLogger('ai.fab');

const FAB_ID = 'aiChatFab';
const BADGE_FIRST_SEEN_KEY = 'c2c_aiFab_firstSeenAt';
const BADGE_TTL_MS = 14 * 24 * 60 * 60 * 1000;  // 14 days

let mounted = false;

/** Should the "חדש" badge still appear? Hides after 14 days.
 *  Note: the new branded icon already has an "Ai" badge top-right and is
 *  visually distinct, so we put the "חדש" pill on the BOTTOM-LEFT (away
 *  from the Ai badge) so they don't overlap. */
function shouldShowNewBadge() {
  try {
    const raw = Number(localStorage.getItem(BADGE_FIRST_SEEN_KEY) || 0);
    if (!Number.isFinite(raw) || raw <= 0) {
      localStorage.setItem(BADGE_FIRST_SEEN_KEY, String(Date.now()));
      return true;
    }
    return Date.now() - raw < BADGE_TTL_MS;
  } catch (_) { return true; }
}

/**
 * Mount the FAB once. Safe to call from bootstrap.js even before the DOM
 * fully loads — we attach a `DOMContentLoaded` listener if needed.
 */
export function mount() {
  if (mounted) return;
  if (typeof document === 'undefined') return;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
    return;
  }
  if (document.getElementById(FAB_ID)) {
    mounted = true;
    return;
  }
  mounted = true;

  const fab = document.createElement('button');
  fab.id = FAB_ID;
  fab.type = 'button';
  fab.className = 'ai-fab';
  fab.setAttribute('aria-label', 'שאל את AutoMe AI');
  fab.innerHTML = `
    ${iconHtml(60)}
    ${shouldShowNewBadge() ? '<span class="ai-fab-badge">חדש</span>' : ''}
  `;
  fab.addEventListener('click', () => {
    try { view.open(); }
    catch (e) { log.warn('open failed:', e); }
  });

  document.body.appendChild(fab);

  // Auth-gated visibility — guests (login screen) should NOT see the AI
  // FAB. Start hidden; reveal only once auth resolves with a user.
  setVisible(false);
  try {
    if (auth().currentUser) setVisible(true);
  } catch (_) { /* firebase not ready yet — the bus events below cover us */ }
  bus.on('auth:ready', () => setVisible(true));
  bus.on('auth:logged-out', () => setVisible(false));

  log.info('mounted');
}

/** Imperatively show/hide. Used by screen transitions that want full screen,
 *  and by the auth listeners (mount) to gate visibility to signed-in users. */
export function setVisible(visible) {
  document.body.classList.toggle('hide-ai-fab', !visible);
}
